import type { HeadCard } from "@/components/Headline";
import { formatMonthShort } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import { keptOver, monthlyFlows, type Plan } from "@/plan-logic/plan-view";

/** A month or a year that kept money says so in green; one that ate into savings, in rose (v46). */
function kept(v: number): Pick<HeadCard, "v" | "tone"> {
  return v >= 0
    ? { v: `${money(v)} saved`, tone: "ok" }
    : { v: `${money(Math.abs(v))} excess spending`, tone: "debt" };
}

/**
 * The cash flow headline (v45/v46 `headCash`), and Budget's third card. The prototype reads this
 * month and the last twelve from bank transactions; until those are connected, "now" and "heading"
 * are the plan's coming month and year on the path you're on (the current-trajectory run), so the
 * twelve-month figure divided by twelve is the "from" of the Abi card, as in the prototype.
 */
export function cashHeadCards(abi: Plan, naive: Plan): HeadCard[] {
  const base = naive ?? abi;
  const m = keptOver(base, 1);
  const y = keptOver(base, 12);
  const first = monthlyFlows(base, 12);
  const span =
    first.length > 1
      ? `${formatMonthShort(first[0].month)} to ${formatMonthShort(first[first.length - 1].month)}`
      : "";
  const path = naive ? ", on the path you're on" : ", on Abi’s plan";
  const cards: HeadCard[] = [
    {
      k: "This month",
      ...kept(m.total),
      s: `${money(m.moneyIn)} in, ${money(m.moneyOut)} out${path}`,
      testId: "hd-cf-month",
    },
    {
      k: "Next 12 months",
      ...kept(y.total),
      s: `${money(y.moneyIn)} in, ${money(y.moneyOut)} out${span ? ` · ${span}` : ""}`,
    },
  ];
  if (naive) cards.push(abiKeptCard(naive, abi));
  return cards;
}

/** "With Abi’s plan: $1,000 → $3,034 savings/month" (Cash flow and Budget: the same money). */
export function abiKeptCard(naive: Plan, abi: Plan, unit = "savings/month"): HeadCard {
  const now = keptOver(naive, 12).monthly;
  const plan = keptOver(abi, 12).monthly;
  const d = plan - now;
  return {
    k: "With Abi’s plan",
    abi: true,
    from: money(now),
    to: `${money(plan)} ${unit}`,
    lead: `${d >= 0 ? "+" : "−"}${money(Math.abs(d))} ${unit}`,
    leadTone: Math.round(d) > 0 ? "ok" : Math.round(d) < 0 ? "debt" : "flat",
    s: Math.round(d) > 0 ? "without increasing your income" : "on the same income",
    testId: "hd-abi",
  };
}
